import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adressenApi } from '../api/adressen';
import SortTh from '../components/SortTh';
import Spinner from '../components/Spinner';
import EmptyTableRow from '../components/EmptyTableRow';
import Pagination from '../components/Pagination';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useColumnConfig, useModuleVisibility } from '../hooks/useConfigVisibility';

export default function AdressenList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useSessionFilter<string>('adressen_searchText', '');
  const [sortBy, setSortBy] = useSessionFilter<string>('adressen_sortBy', 'strasse');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('adressen_sortDir', 'asc');
  const cols = useColumnConfig('adressen');
  const pv = useModuleVisibility();
  const perPage = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['adressen', { page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir }],
    queryFn: () => adressenApi.getAdressen({ page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir }),
  });

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <input
            type="text"
            placeholder="Suche nach Strasse, PLZ, Stadt..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
          />
        </div>

        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortTh field="strasse" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Straße</SortTh>
                    {cols.plz !== false && <SortTh field="plz" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>PLZ</SortTh>}
                    {cols.stadt !== false && <SortTh field="stadt" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Stadt</SortTh>}
                    {cols.haeuser !== false && pv.haeuser !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Häuser</th>}
                    {cols.verfahren !== false && pv.verfahren !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verfahren</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items?.map((a) => (
                    <tr
                      key={a.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/adressen/${a.id}`)}
                    >
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        {a.strasse} {a.hausnummer}
                      </td>
                      {cols.plz !== false && <td className="px-3 py-3 text-sm text-gray-500">{a.plz || '-'}</td>}
                      {cols.stadt !== false && <td className="px-3 py-3 text-sm text-gray-500">{a.stadt || '-'}</td>}
                      {cols.haeuser !== false && pv.haeuser !== false && (
                        <td className="px-3 py-3 text-sm">
                          {a.haeuser_count > 0 ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {a.haeuser_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      )}
                      {cols.verfahren !== false && pv.verfahren !== false && (
                        <td className="px-3 py-3 text-sm">
                          {a.verfahren_count > 0 ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                              {a.verfahren_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {(!data?.items || data.items.length === 0) && (
                    <EmptyTableRow colSpan={5} message="Keine Adressen gefunden." />
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={data?.total ?? 0}
              entityLabel="Adressen"
              onPageChange={setPage}
            />
          </>
        )}
      </main>
    </div>
  );
}
